import React, { useState, useMemo } from 'react';
import { TextField, Typography, EmptyState, Icon } from '@scale/scaleui';
import { twMerge } from 'tailwind-merge';
import { EmailTemplateData } from '@scale/shared-types';
import TemplatePreview from './TemplatePreview';
import { Alert } from 'design-system';

interface EmailTemplateSelectionProps {
  selectedTemplate?: EmailTemplateData;
  setSelectedTemplate: (val: EmailTemplateData | undefined) => void;
  rawTemplates: EmailTemplateData[];
  showSwitchTemplateWarning: boolean;
}

const EmailTemplateSelection = ({
  selectedTemplate,
  setSelectedTemplate,
  rawTemplates,
  showSwitchTemplateWarning,
}: EmailTemplateSelectionProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const templates = useMemo(() => {
    if (rawTemplates && searchTerm.length > 0) {
      return rawTemplates.filter(
        rawTemplate =>
          rawTemplate.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          rawTemplate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          rawTemplate.body.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    return rawTemplates ?? [];
  }, [rawTemplates, searchTerm]);

  return (
    <div className="flex flex-col gap-4 h-full">
      <TextField
        placeholder="Search templates..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        leadingIcon="search"
      />
      <Typography.Paragraph2 className="text-neutral-400">
        {templates.length} templates
      </Typography.Paragraph2>
      <div className="flex w-full h-full gap-4">
        <div className="relative w-[500px]">
          <div className="absolute h-full overflow-auto w-full">
            {templates.map((template: EmailTemplateData, index: number) => (
              <div
                key={`template-${index}`}
                className="py-8 px-4 hover:bg-neutral-50 flex gap-2 border-t border-solid border-neutral-200 w-full"
                onClick={() => setSelectedTemplate(templates[index])}
              >
                <Icon
                  className={twMerge(templates[index] !== selectedTemplate && 'invisible')}
                  icon={['fas', 'check']}
                  size="lg"
                  variant="primary"
                />
                <div className="w-full overflow-hidden">
                  <Typography.Subtitle2 className="truncate">{template.name}</Typography.Subtitle2>
                  <Typography.Paragraph3 className="truncate">
                    {template.subject}
                  </Typography.Paragraph3>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative flex-grow">
          {selectedTemplate ? (
            <TemplatePreview
              className="absolute"
              templateHTML={selectedTemplate.htmlWrapper}
              content={selectedTemplate.body}
              subject={selectedTemplate.subject}
              sendgridTemplateId={selectedTemplate.sendgridTemplateId}
            />
          ) : (
            <EmptyState
              className="absolute h-full w-full flex items-center justify-center"
              hasBackground
              icon="file"
              title="Select a template"
            />
          )}
        </div>
      </div>
      {showSwitchTemplateWarning && (
        <Alert severity="warning">
          Switching to a new template will override any changes you made previously.
        </Alert>
      )}
    </div>
  );
};

export default EmailTemplateSelection;
